import {useCallback, useEffect, useMemo, useState} from 'react';

/**
 * Shared state for the Classification Level / Distribution / Compartment fields rendered by
 * `ClassificationFieldsGroup` in every "Add source" / "Add Material" dialog (Files, URL, Raw
 * text × Sources / Collection Material). Replaces the state/reset/payload boilerplate each of
 * those six dialogs used to hand-roll individually.
 *
 * @param {string} [initialLevel='U'] - Default classification level. Collection Material
 *   dialogs pass the parent task's level (which can change between opens); Sources dialogs pass
 *   a fixed default from `useSourcesUploadQueueStore`.
 * @param {object} [options]
 * @param {boolean} [options.open] - When provided, re-syncs `level` to `initialLevel` every time
 *   the dialog transitions to open, so switching parents (e.g. Collection Tasks) doesn't leak the
 *   previous default. Omit for dialogs whose `initialLevel` never changes after mount.
 */
export default function useClassificationFields(initialLevel = 'U', {open} = {}) {
    const [level, setLevel] = useState(initialLevel || 'U');
    const [distribution, setDistribution] = useState([]);
    const [compartment, setCompartment] = useState([]);

    useEffect(() => {
        if (open) {
            setLevel(initialLevel || 'U');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, initialLevel]);

    const reset = useCallback(() => {
        setLevel(initialLevel || 'U');
        setDistribution([]);
        setCompartment([]);
    }, [initialLevel]);

    // Payload shape expected by the backend's `classificationLevel` + repeated
    // `distribution`/`compartment` params (Collection Material form fields) or JSON body
    // (Sources' web imports) — omits fields that weren't set rather than sending empty values.
    const toPayload = useCallback(() => ({
        classificationLevel: level || undefined,
        distribution: distribution.length ? distribution : undefined,
        compartment: compartment.length ? compartment : undefined,
    }), [level, distribution, compartment]);

    const fieldsProps = useMemo(() => ({
        level,
        onLevelChange: setLevel,
        distribution,
        onDistributionChange: setDistribution,
        compartment,
        onCompartmentChange: setCompartment,
    }), [level, distribution, compartment]);

    return {level, setLevel, distribution, setDistribution, compartment, setCompartment, reset, toPayload, fieldsProps};
}
